import {
    IMAGE_POSITION_OPTIONS,
    ImagePosition,
} from "constants/notebooks/image-position";
import { IMAGE_SIZE_OPTIONS, ImageSize } from "constants/notebooks/image-size";
import { useImageStyles } from "molecules/page-block-content/image-block/use-image-styles";
import React, { useState } from "react";
import { useAtomValue } from "jotai";
import { imageLoadingStrategyAtom } from "utilities/atoms/image-loading-strategy-atom";
import ImageAnnotationList from "organisms/image-annotations/image-annotation-list";
import { ImageAnnotationRecord } from "models/view-models/block-configurations/image-annotation-record";
import ImageAnnotation from "molecules/page-block-content/image-block/image-annotation";
import useManageImageAnnotations from "organisms/image-annotations/use-manage-image-annotations";
import Loader from "atoms/loaders/loader";
import ImageAnnotationsModal from "organisms/image-annotations/image-annotations-modal";

const CSS_CLASS_NAME = "c-image-block-image-display";

interface ImageBlockImageDisplayProps {
    imageFilePath: string;
    size?: ImageSize;
    position?: ImagePosition;
    altText?: string;
    imageAnnotations?: ImageAnnotationRecord[];
    onClick?: (e: React.MouseEvent<HTMLImageElement, MouseEvent>) => void;
    onImageAnnotationRemoved?: (
        removedImageAnnotation: ImageAnnotationRecord | null | undefined
    ) => Promise<void>;
    onImageAnnotationSaved?: (
        updatedImageAnnotation: ImageAnnotationRecord | null | undefined
    ) => Promise<void>;
    onImageAnnotationMoved?: (
        updatedImageAnnotation: ImageAnnotationRecord | null | undefined
    ) => Promise<void>;
    isEditable?: boolean;
    printable?: boolean;
}

const ImageBlockImageDisplay = ({
    size,
    position,
    imageFilePath,
    altText,
    imageAnnotations = [],
    onClick,
    onImageAnnotationMoved,
    onImageAnnotationRemoved,
    onImageAnnotationSaved,
    isEditable,
    printable,
}: ImageBlockImageDisplayProps) => {
    const { sizeOrDefault, positionOrDefault } = useImageStyles(size, position);
    const imageLoadingStrategy = useAtomValue(imageLoadingStrategyAtom);

    const {
        onMoveStart,
        moveAnnotationToNewPosition,
        onMoveEnd,
        reset,
        activeImageAnnotation,
        showImageAnnotationModal,
        handleEditExistingImageAnnotation,
    } = useManageImageAnnotations();

    const handleImageAnnotationDropped = (
        e: React.DragEvent<HTMLImageElement>
    ) => {
        const movedAnnotation = moveAnnotationToNewPosition(e);
        onImageAnnotationMoved && onImageAnnotationMoved(movedAnnotation);
        onMoveEnd();
    };

    const [imgLoaded, setImgLoaded] = useState(false);

    return (
        <div
            className={`${CSS_CLASS_NAME} ${IMAGE_POSITION_OPTIONS[positionOrDefault]}`}>
            <div
                className={`${CSS_CLASS_NAME}__imageWrapper ${IMAGE_SIZE_OPTIONS[sizeOrDefault]}${!imgLoaded ? " -loading" : ""}`}>
                {!imgLoaded && (
                    <div className={`${CSS_CLASS_NAME}__loader`}>
                        <Loader />
                    </div>
                )}

                <img
                    onDrop={handleImageAnnotationDropped}
                    onClick={onClick}
                    className={`${CSS_CLASS_NAME}__image${imgLoaded ? " -loaded" : ""}`}
                    loading={imageLoadingStrategy}
                    src={imageFilePath}
                    alt={altText}
                    onLoad={() => setImgLoaded(true)}
                />

                {imageAnnotations.map((annotation, i) => (
                    <ImageAnnotation
                        onClick={handleEditExistingImageAnnotation}
                        canDrag={!!isEditable}
                        onMoveStart={onMoveStart}
                        key={annotation.id}
                        annotation={annotation}
                        number={i + 1}
                        visible={imgLoaded}
                        printable={printable}
                    />
                ))}
            </div>
            <ImageAnnotationList
                isEditable={isEditable}
                imageAnnotations={imageAnnotations}
                onUpdated={onImageAnnotationSaved}
                onDeleted={onImageAnnotationRemoved}
            />
            <ImageAnnotationsModal
                onCancel={reset}
                onClose={reset}
                onSubmit={onImageAnnotationSaved}
                isVisible={showImageAnnotationModal}
                imageAnnotation={activeImageAnnotation}
            />
        </div>
    );
};

export default ImageBlockImageDisplay;
